import React from "react";
import {
  Grid,
  Box,
  Typography,
  TextField,
  Button,
  FormControlLabel,
  FormControl,
  Checkbox,
  Select,
  MenuItem,
  InputLabel,
  CardHeader,
  Card,
  CardContent,
  Tooltip,
  IconButton,
  Divider,
  FormHelperText,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";

const ClientContact = ({
  clientContacts,
  handleContactChange,
  handleAddContact,
  handleRemoveContact,
  handleContactDetailsChange,
  handleAddNumber,
  handleRemoveNumber,
  handleAddEmail,
  handleRemoveEmail,
  handleContactCheckboxChange,
  loading,
  isEdit,
  errors,
}) => {
  const renderContactFields = (contact, index) => {
    const firstNameError = errors[`contacts[${index}].firstname`];
    const lastNameError = errors[`contacts[${index}].lastname`];

    return (
      <Card key={index} sx={{ marginBottom: "16px" }} variant="outlined">
        <CardContent sx={{ padding: "16px" }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="First Name"
                fullWidth
                value={contact.firstname}
                onChange={(e) => handleContactChange(index, "firstname", e.target.value)}
                required
                size="small"
                disabled={!isEdit || loading}
                error={!!firstNameError}
                helperText={firstNameError}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Last Name"
                fullWidth
                value={contact.lastname}
                onChange={(e) => handleContactChange(index, "lastname", e.target.value)}
                required
                size="small"
                disabled={!isEdit || loading}
                error={!!lastNameError}
                helperText={lastNameError}
              />
            </Grid>

            {contact.numbers.map((number, numIdx) => {
              const typeError = errors[`contacts[${index}].numbers[${numIdx}].type`];
              const numberError = errors[`contacts[${index}].numbers[${numIdx}].number`];
              return (
                <React.Fragment key={numIdx}>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small" error={!!typeError}>
                      <InputLabel id={`contact-type-label-${index}-${numIdx}`}>Contact Type</InputLabel>
                      <Select
                        labelId={`contact-type-label-${index}-${numIdx}`}
                        value={number.type}
                        onChange={(e) => handleContactDetailsChange(index, "numbers", numIdx, "type", e.target.value)}
                        label="Contact Type"
                        disabled={!isEdit || loading}
                      >
                        <MenuItem value="mobile">Mobile</MenuItem>
                        <MenuItem value="landline">Landline</MenuItem>
                      </Select>
                      {typeError && <FormHelperText>{typeError}</FormHelperText>}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Contact Number"
                      fullWidth
                      value={number.number}
                      onChange={(e) => handleContactDetailsChange(index, "numbers", numIdx, "number", e.target.value)}
                      size="small"
                      disabled={!isEdit || loading}
                      error={!!numberError}
                      helperText={numberError}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box display="flex" alignItems="center" sx={{ height: '40px' }}>
                      <Tooltip title="Delete Number">
                        <span>
                          <IconButton onClick={() => handleRemoveNumber(index, numIdx)} disabled={!isEdit || loading} size="small">
                            <DeleteIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Grid>
                </React.Fragment>
              );
            })}

            {contact.emails.map((email, emailIdx) => {
              const emailError = errors[`contacts[${index}].emails[${emailIdx}].email`];
              return (
                <React.Fragment key={emailIdx}>
                  <Grid item xs={12} md={8}>
                    <TextField
                      label="Email Address"
                      fullWidth
                      value={email.email}
                      onChange={(e) => handleContactDetailsChange(index, "emails", emailIdx, "email", e.target.value)}
                      size="small"
                      disabled={!isEdit || loading}
                      error={!!emailError}
                      helperText={emailError}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box display="flex" alignItems="center" sx={{ height: '40px' }}>
                      <Tooltip title="Delete Email">
                        <span>
                          <IconButton onClick={() => handleRemoveEmail(index, emailIdx)} disabled={!isEdit || loading} size="small">
                            <DeleteIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Grid>
                </React.Fragment>
              );
            })}

            <Grid item container spacing={2} alignItems="center" sx={{ mt: 0 }}>
              <Grid item xs={12} sm={4} md={3}>
                <FormControlLabel
                  control={<Checkbox checked={contact.billing} onChange={() => handleContactCheckboxChange(index)} disabled={!isEdit || loading} size="small" />}
                  label="Set as Billing Contact"
                  sx={{
                    margin: 0,
                    '& .MuiTypography-root': {
                      fontSize: '0.875rem'
                    },
                    '& .MuiCheckbox-root': {
                      padding: '4px'
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4} md={3}>
                <Button
                  onClick={() => handleAddNumber(index)}
                  size="small"
                  sx={{ fontSize: "0.875rem", width: "100%" }}
                  disabled={!isEdit || loading}
                >
                  Add Another Number
                </Button>
              </Grid>

              <Grid item xs={12} sm={4} md={3}>
                <Button
                  onClick={() => handleAddEmail(index)}
                  size="small"
                  sx={{ fontSize: "0.875rem", width: "100%" }}
                  disabled={!isEdit || loading}
                >
                  Add Another Email
                </Button>
              </Grid>

              {clientContacts.length > 1 && (
                <Grid item xs={12} sm={12} md={3}>
                  <Button
                    color="error"
                    size="small"
                    onClick={() => handleRemoveContact(index)}
                    disabled={!isEdit || loading}
                    sx={{ width: "100%" }}
                  >
                    Remove Contact Person
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  return (
    <Grid container>
      <CardHeader title={<Typography variant="h6">Contact Person</Typography>} />
      <Grid item xs={12}>
        {clientContacts.map((contact, idx) => renderContactFields(contact, idx))}
      </Grid>
      <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddContact} disabled={!isEdit || loading}>
        Add Contact Person
      </Button>
    </Grid>
  );
};

export default ClientContact;
